import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import {
  matchesPermission,
  permissionCounts,
  searchableName,
  selectionState,
} from "./permissionUtils";

export default function PermissionTree({
  node,
  permissions,
  onToggle,
  query,
  selectedOnly,
  disabled,
}) {
  const [expanded, setExpanded] = useState({});
  const renderNode = (item, level = 0, ancestorMatches = false) => {
    if (!matchesPermission(item, query, selectedOnly, permissions, ancestorMatches)) return null;
    const state = selectionState(item, permissions);
    const hasChildren = item.children.length > 0;
    const open = Boolean(query || selectedOnly || expanded[item.key] || level === 0);
    const matchesName =
      ancestorMatches || !query || searchableName(item.name).includes(searchableName(query));
    const count = permissionCounts(item, permissions);
    return (
      <Box
        key={item.key}
        sx={{
          border: hasChildren && level === 1 ? "1px solid" : "none",
          borderColor: "divider",
          borderRadius: 2,
          mb: hasChildren && level === 1 ? 1.5 : 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            pl: level ? 2 : 0,
            py: hasChildren ? 0.9 : 0.75,
            pr: 1,
            borderRadius: 1.5,
            mb: hasChildren && level === 0 ? 1 : 0,
            bgcolor: hasChildren
              ? "rgba(26,115,232,0.045)"
              : state.checked
              ? "rgba(26,115,232,0.035)"
              : "transparent",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          {hasChildren && level > 0 ? (
            <IconButton
              size="small"
              aria-label={`${open ? "Contraer" : "Expandir"} ${item.name}`}
              aria-expanded={open}
              onClick={() => setExpanded((previous) => ({ ...previous, [item.key]: !open }))}
              disabled={Boolean(query || selectedOnly)}
            >
              {open ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
          ) : (
            <Box sx={{ width: level ? 30 : 0, flexShrink: 0 }} />
          )}
          <FormControlLabel
            sx={{
              m: 0,
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              "& .MuiFormControlLabel-label": { ml: 0, transform: "none", lineHeight: 1.4 },
            }}
            control={
              <Checkbox
                size="small"
                sx={{
                  p: 0.75,
                  "&.MuiCheckbox-indeterminate .MuiSvgIcon-root": {
                    color: "#fff",
                    bgcolor: "info.main",
                    borderColor: "info.main",
                    backgroundImage: "none",
                  },
                }}
                checked={state.checked}
                indeterminate={state.indeterminate}
                onChange={() => onToggle(item.key)}
                disabled={disabled}
                inputProps={{ "aria-label": item.name }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{ fontWeight: hasChildren ? 700 : 500, fontSize: "0.8125rem" }}
              >
                {level === 0 ? "Seleccionar todo el módulo" : item.name}
              </Typography>
            }
          />
          {hasChildren && (
            <Chip
              size="small"
              label={`${count.selected}/${count.total}`}
              sx={{
                height: 24,
                mr: 1,
                borderRadius: 1.5,
                bgcolor: "rgba(26,115,232,0.08)",
                color: "info.main",
                fontSize: "0.68rem",
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        {hasChildren && (
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                ml: level ? 2 : 0,
                borderLeft: level ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              {item.children.map((child) => renderNode(child, level + 1, matchesName))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };
  return renderNode(node);
}
PermissionTree.propTypes = {
  node: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  selectedOnly: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
};
